import { useCallback, useEffect, useState } from 'react';
import { Title } from 'react-admin';
import { Link, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { useHasPermission } from '../PermissionGate';
import { useIntakeSide } from '../intake/intakeSides';
import { fmtDateTimeShort } from '../../shared/listCells';
import { FuelSurchargeSection } from '../fuelSurcharges/FuelSurchargeSection';
import { AVIZATION_RESULTS, CONTACT_CHANNELS } from '../csWork/csWorkApi';
import {
  FEATURE_ORDER_SETTLEMENT,
  MAX_REASON_LENGTH,
  MIN_REASON_LENGTH,
  addCorrection,
  decideStop,
  fetchSettlement,
  fetchSettlements,
  handToInvoicing,
  hoursText,
  money,
  parseAmountInput,
  toErrorMessage,
  type SettlementDetail,
  type SettlementList,
  type SettlementListState,
} from './settlementApi';

/**
 * Rozliczenie zlecenia (prace 26.10.2026, pakiet P4b; makieta p. Mariusza
 * „Rozliczenie”, BR-ADM-12, ścieżka ADM-05; przełącznik `orderSettlement`,
 * S-26.10-12). Lista zleceń zrealizowanych do rozliczenia i przekazanych do
 * fakturowania; przy zleceniu pozycje, postój ze śladu GPS (szacunek),
 * decyzje Administracji i historia. „Przekazane do fakturowania” to status
 * w aplikacji — faktura powstaje poza nią. Czynności widzi tylko
 * Administracja; rozstrzyga API.
 */

export const FEATURE_OFF_NOTE = 'Rozliczenie zleceń jest wyłączone w wersji aplikacji, z której korzystasz.';
export const NO_PERMISSION_NOTE = 'Rozliczenie jest dla Administracji i CS.';
export const LOAD_ERROR = 'Nie udało się wczytać rozliczeń. Odśwież stronę.';

const STATE_CHOICES: ReadonlyArray<{ id: SettlementListState; name: string }> = [
  { id: 'open', name: 'Do rozliczenia' },
  { id: 'invoicing', name: 'Przekazane do fakturowania' },
];

const CURRENCIES = ['EUR', 'PLN'];

const channelName = (id: string | null) => CONTACT_CHANNELS.find((c) => c.id === id)?.name ?? id ?? '—';
const resultName = (id: string | null) => AVIZATION_RESULTS.find((c) => c.id === id)?.name ?? id ?? '—';

export const SettlementScreen = () => {
  const on = useFeatureState(FEATURE_ORDER_SETTLEMENT);
  const canIntake = useHasPermission('ORDER_INTAKE');
  if (on === undefined) return <FeaturesLoading />;
  if (!on || !canIntake) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={menuLabel('settlements')} />
        <Alert severity="info">{on ? NO_PERMISSION_NOTE : FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <SettlementWorkspace />;
};

/** `?id=` w adresie otwiera rozliczenie zlecenia (odnośnik z karty, przewodnik, e2e). */
function idFromSearch(search: string): number | null {
  const raw = new URLSearchParams(search).get('id');
  const n = raw ? Number(raw) : NaN;
  return Number.isInteger(n) && n > 0 ? n : null;
}

const SettlementWorkspace = () => {
  const location = useLocation();
  const [state, setState] = useState<SettlementListState>('open');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState<SettlementList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(() => idFromSearch(location.search));
  const [reloadKey, setReloadKey] = useState(0);

  // Odnośnik `?id=` otwarty, gdy ekran już stoi (inne zlecenie z karty, z pulpitu) — ekran się nie
  // montuje od nowa, więc wybór trzeba wziąć z adresu.
  useEffect(() => {
    const id = idFromSearch(location.search);
    if (id !== null) setSelected(id);
  }, [location.search]);

  useEffect(() => {
    let active = true;
    const t = setTimeout(() => {
      fetchSettlements({ state, q: q.trim() || undefined, page })
        .then((l) => {
          if (active) {
            setList(l);
            setError(null);
          }
        })
        .catch(() => {
          if (active) setError(LOAD_ERROR);
        });
    }, 250);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [state, q, page, reloadKey]);

  const pages = list ? Math.max(1, Math.ceil(list.total / list.perPage)) : 1;

  return (
    <Stack spacing={2} sx={{ p: 2 }} data-testid="settlement-screen">
      <Title title={menuLabel('settlements')} />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { md: 'center' } }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={state}
          onChange={(_, v: SettlementListState | null) => {
            if (v) {
              setState(v);
              setPage(1);
            }
          }}
        >
          {STATE_CHOICES.map((c) => (
            <ToggleButton key={c.id} value={c.id} data-testid={`settlement-state-${c.id}`}>
              {c.name}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          size="small"
          label="Szukaj: numer zlecenia albo klient"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          slotProps={{ htmlInput: { 'data-testid': 'settlement-search' } }}
          sx={{ minWidth: { md: 320 } }}
        />
      </Stack>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Card variant="outlined">
        <CardContent>
          {!list ? (
            <Typography variant="body2">Wczytuję…</Typography>
          ) : list.rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="settlement-empty">
              {state === 'open'
                ? `Brak zrealizowanych zleceń do rozliczenia z ostatnich ${list.windowDays} dni.`
                : 'Nic nie przekazano jeszcze do fakturowania.'}
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" data-testid="settlement-list">
                <TableHead>
                  <TableRow>
                    <TableCell>Zlecenie</TableCell>
                    <TableCell>Klient</TableCell>
                    <TableCell>Zakończone</TableCell>
                    <TableCell>Do decyzji</TableCell>
                    <TableCell>Stan</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.rows.map((r) => (
                    <TableRow
                      key={r.id}
                      hover
                      selected={r.id === selected}
                      onClick={() => setSelected(r.id)}
                      sx={{ cursor: 'pointer' }}
                      data-testid="settlement-row"
                      data-id={r.id}
                    >
                      <TableCell>{r.number ?? `#${r.id}`}</TableCell>
                      <TableCell>{r.client ?? '—'}</TableCell>
                      <TableCell>
                        {fmtDateTimeShort(r.finishedAt)}
                        {r.place ? ` · ${r.place}` : ''}
                      </TableCell>
                      <TableCell>
                        {r.stopPending ? <Chip size="small" color="warning" label="postój" /> : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={r.status === 'invoicing' ? 'success' : 'warning'}
                          label={r.statusLabel}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {list && pages > 1 ? (
            <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Poprzednie
              </Button>
              <Typography variant="caption">
                strona {page} z {pages} · {list.total} zleceń
              </Typography>
              <Button size="small" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                Następne
              </Button>
            </Stack>
          ) : null}
        </CardContent>
      </Card>
      {selected !== null ? (
        <SettlementPanel id={selected} onChanged={() => setReloadKey((k) => k + 1)} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          Wybierz zlecenie z listy, żeby zobaczyć pozycje rozliczenia.
        </Typography>
      )}
    </Stack>
  );
};

type DialogKind = 'waive' | 'correction' | 'invoicing' | null;

export const SettlementPanel = ({ id, onChanged }: { id: number; onChanged?: () => void }) => {
  const isAdministration = useIntakeSide('administration');
  const [detail, setDetail] = useState<SettlementDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<DialogKind>(null);

  useEffect(() => {
    let active = true;
    setDetail(null);
    setError(null);
    fetchSettlement(id)
      .then((d) => {
        if (active) setDetail(d);
      })
      .catch((e) => {
        if (active) setError(toErrorMessage(e));
      });
    return () => {
      active = false;
    };
  }, [id]);

  const openDialog = (kind: DialogKind) => {
    setActionError(null);
    setDialog(kind);
  };

  const run = useCallback(
    async (action: () => Promise<SettlementDetail>) => {
      setBusy(true);
      setActionError(null);
      try {
        setDetail(await action());
        setDialog(null);
        onChanged?.();
      } catch (e) {
        setActionError(toErrorMessage(e));
      } finally {
        setBusy(false);
      }
    },
    [onChanged],
  );

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!detail) return <Typography variant="body2">Wczytuję rozliczenie…</Typography>;

  const d = detail;
  const invoiced = d.status === 'invoicing';
  return (
    <Stack spacing={2} data-testid="settlement-panel">
      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }} useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Zlecenie
              </Typography>
              <Typography variant="body1">
                <Link to={`/user-offers/${d.order.id}/show`}>{d.order.number ?? `#${d.order.id}`}</Link>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {d.order.relation}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Klient
              </Typography>
              <Typography variant="body1">{d.order.client ?? '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Okno klienta
              </Typography>
              <Typography variant="body1">
                {fmtDateTimeShort(d.order.fromDate)} – {fmtDateTimeShort(d.order.toDate)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Wykonawca
              </Typography>
              <Typography variant="body1">{d.order.vehicle ?? d.order.carrier ?? '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Stan
              </Typography>
              <Box>
                <Chip
                  size="small"
                  color={invoiced ? 'success' : 'warning'}
                  label={d.statusLabel}
                  data-testid="settlement-status"
                />
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1.4fr) minmax(0,1fr)' } }}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Pozycje rozliczenia
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" data-testid="settlement-lines">
                <TableHead>
                  <TableRow>
                    <TableCell>Pozycja</TableCell>
                    <TableCell>Źródło</TableCell>
                    <TableCell align="right">Kwota</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {d.lines.map((l, i) => (
                    <TableRow key={`${l.kind}-${i}`} data-testid={`settlement-line-${l.kind}`}>
                      <TableCell>{l.label}</TableCell>
                      <TableCell>
                        <Typography variant="caption">{l.source}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {l.amount === null ? (l.kind === 'stop' ? 'do decyzji' : '—') : money(l.amount, l.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow data-testid="settlement-total">
                    <TableCell>
                      <b>Razem</b>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {d.totals.length > 1 ? 'każda waluta osobno, bez przeliczania' : ''}
                        {d.incomplete ? ' bez pozycji, których kwoty brakuje' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <b>{d.totals.length === 0 ? '—' : d.totals.map((t) => money(t.amount, t.currency)).join(' + ')}</b>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
            <StopSection d={d} />
            {actionError ? (
              <Alert severity="error" sx={{ mt: 1 }} data-testid="settlement-action-error">
                {actionError}
              </Alert>
            ) : null}
            {invoiced ? (
              <Alert severity="success" sx={{ mt: 1 }} data-testid="settlement-invoiced">
                Rozliczenie przekazane do fakturowania
                {d.invoicing ? ` — ${d.invoicing.by ?? 'nieznany autor'}, ${fmtDateTimeShort(d.invoicing.at)}` : ''}
                {d.invoicing?.note ? `. ${d.invoicing.note}` : ''}. Fakturę wystawia się poza aplikacją.
              </Alert>
            ) : isAdministration ? (
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
                {!d.stopDecision ? (
                  <>
                    <ActionButton
                      label="Nalicz postój"
                      testId="adm-rozliczenie-postoj-nalicz"
                      refusal={d.actions.charge}
                      busy={busy}
                      onClick={() => run(() => decideStop(d.order.id, 'charge'))}
                    />
                    <ActionButton
                      label="Nie naliczaj"
                      testId="settlement-stop-waive"
                      refusal={d.actions.waive}
                      busy={busy}
                      onClick={() => openDialog('waive')}
                    />
                  </>
                ) : null}
                <ActionButton
                  label="Dodaj korektę"
                  testId="settlement-correction-add"
                  refusal={d.actions.correction}
                  busy={busy}
                  onClick={() => openDialog('correction')}
                />
                <ActionButton
                  label="Przekaż do fakturowania"
                  testId="adm-rozliczenie-fakturuj"
                  refusal={d.actions.invoicing}
                  busy={busy}
                  primary
                  onClick={() => openDialog('invoicing')}
                />
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                Decyzje o postoju, korekty i przekazanie do fakturowania należą do Administracji.
              </Typography>
            )}
          </CardContent>
        </Card>
        <Stack spacing={2}>
          <FuelSurchargeSection kind="order" id={d.order.id} />
          <Card variant="outlined" data-testid="settlement-avizations">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Awizacje
              </Typography>
              {d.avizations.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  CS nie wpisał awizacji przy tym zleceniu.
                </Typography>
              ) : (
                d.avizations.map((a) => (
                  <Typography key={a.id} variant="body2">
                    {fmtDateTimeShort(a.at)} · {channelName(a.channel)} · {resultName(a.result)}
                    {a.by ? ` · ${a.by}` : ''}
                  </Typography>
                ))
              )}
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                Informacja — stawki dodatków (awizacja, dodatek paliwowy, wózek) czekają na katalog dodatków
                klienta; kwotę dolicz korektą, jeśli należy się klientowi.
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Dokumenty
              </Typography>
              <Typography variant="body2" color="text.secondary">
                List przewozowy CMR i potwierdzenie rozładunku są poza aplikacją — sprawdź je w obiegu dokumentów
                przed przekazaniem do fakturowania.
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" data-testid="settlement-history">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Historia rozliczenia
              </Typography>
              {d.history.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nikt jeszcze nie podjął decyzji.
                </Typography>
              ) : (
                d.history.map((h) => (
                  <Box key={h.id} sx={{ mb: 1 }} data-testid="settlement-history-entry">
                    <Typography variant="body2">{h.text}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtDateTimeShort(h.at)} · {h.by ?? 'nieznany autor'}
                      {h.reason ? ` · powód: ${h.reason}` : ''}
                    </Typography>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <WaiveDialog
        open={dialog === 'waive'}
        busy={busy}
        error={actionError}
        onClose={() => setDialog(null)}
        onConfirm={(reason) => run(() => decideStop(d.order.id, 'waive', reason))}
      />
      <CorrectionDialog
        open={dialog === 'correction'}
        busy={busy}
        error={actionError}
        defaultCurrency={d.defaultCurrency}
        onClose={() => setDialog(null)}
        onConfirm={(body) => run(() => addCorrection(d.order.id, body))}
      />
      <InvoicingDialog
        open={dialog === 'invoicing'}
        busy={busy}
        error={actionError}
        onClose={() => setDialog(null)}
        onConfirm={(note) => run(() => handToInvoicing(d.order.id, note))}
      />
    </Stack>
  );
};

const ActionButton = ({
  label,
  testId,
  refusal,
  busy,
  primary,
  onClick,
}: {
  label: string;
  testId: string;
  refusal: string | null;
  busy: boolean;
  primary?: boolean;
  onClick: () => void;
}) => (
  <Tooltip title={refusal ?? ''}>
    <span>
      <Button
        size="small"
        variant={primary ? 'contained' : 'outlined'}
        disabled={busy || refusal !== null}
        onClick={onClick}
        data-testid={testId}
      >
        {label}
      </Button>
    </span>
  </Tooltip>
);

const StopSection = ({ d }: { d: SettlementDetail }) => (
  <Box sx={{ mt: 2 }} data-testid="settlement-stop">
    <Typography variant="subtitle2">Postój przy punktach — szacunek ze śladu GPS</Typography>
    <Typography variant="body2" sx={{ mb: 1 }} data-testid="settlement-stop-description">
      {d.stop.description}
    </Typography>
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Punkt</TableCell>
            <TableCell>Na miejscu</TableCell>
            <TableCell>Czas</TableCell>
            <TableCell>Płatne</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {d.stop.points.map((p) => (
            <TableRow key={p.pointOrder} data-testid="settlement-stop-point">
              <TableCell>{p.label}</TableCell>
              {p.measured ? (
                <>
                  <TableCell>
                    {p.arrivedAt ? fmtDateTimeShort(p.arrivedAt) : '—'} – {p.departedAt ? fmtDateTimeShort(p.departedAt) : '—'}
                  </TableCell>
                  <TableCell>{hoursText(p.hours)}</TableCell>
                  <TableCell>{hoursText(p.paidHours)}</TableCell>
                </>
              ) : (
                <TableCell colSpan={3}>
                  <Typography variant="caption" color="text.secondary">
                    brak pomiaru: {p.missingReason}
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
    {d.stopDecision ? (
      <Box sx={{ mt: 1 }} data-testid="settlement-stop-decision">
        <Chip
          size="small"
          color={d.stopDecision.decision === 'charge' ? 'success' : 'default'}
          label={
            d.stopDecision.decision === 'charge'
              ? `${d.stopDecision.label}: ${money(d.stopDecision.amountEur, 'EUR')}`
              : d.stopDecision.label
          }
        />
        <Typography variant="caption" color="text.secondary" component="p">
          {d.stopDecision.by ?? 'nieznany autor'}
          {d.stopDecision.at ? `, ${fmtDateTimeShort(d.stopDecision.at)}` : ''}
          {d.stopDecision.reason ? ` · powód: ${d.stopDecision.reason}` : ''}
        </Typography>
      </Box>
    ) : null}
  </Box>
);

const reasonError = (r: string) =>
  r.trim().length < MIN_REASON_LENGTH
    ? `Co najmniej ${MIN_REASON_LENGTH} znaków`
    : r.trim().length > MAX_REASON_LENGTH
      ? `Najwyżej ${MAX_REASON_LENGTH} znaków`
      : null;

const WaiveDialog = ({
  open,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) => {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  const invalid = reasonError(reason);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Nie naliczaj postoju</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Postój nie trafi do rozliczenia. Powód zostanie w historii zlecenia z Twoim nazwiskiem.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Powód"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          helperText={invalid ?? ' '}
          slotProps={{ htmlInput: { 'data-testid': 'settlement-waive-reason' } }}
        />
        {error ? <Alert severity="error">{error}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button
          variant="contained"
          disabled={busy || invalid !== null}
          onClick={() => onConfirm(reason.trim())}
          data-testid="settlement-waive-confirm"
        >
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CorrectionDialog = ({
  open,
  busy,
  error,
  defaultCurrency,
  onClose,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  error: string | null;
  defaultCurrency: string;
  onClose: () => void;
  onConfirm: (body: { amount: number; currency: string; reason: string }) => void;
}) => {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(defaultCurrency);
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) {
      setAmount('');
      setCurrency(defaultCurrency);
      setReason('');
    }
  }, [open, defaultCurrency]);
  const parsed = parseAmountInput(amount);
  const invalidReason = reasonError(reason);
  const currencies = CURRENCIES.includes(defaultCurrency) ? CURRENCIES : [defaultCurrency, ...CURRENCIES];
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Korekta rozliczenia</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Kwota (ujemna = obniżka)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              helperText={amount !== '' && parsed === null ? 'Liczba różna od zera, najwyżej dwa miejsca po przecinku' : ' '}
              slotProps={{ htmlInput: { 'data-testid': 'settlement-correction-amount', inputMode: 'decimal' } }}
            />
            <TextField
              select
              label="Waluta"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              slotProps={{ htmlInput: { 'data-testid': 'settlement-correction-currency' } }}
              sx={{ minWidth: 110 }}
            >
              {currencies.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            fullWidth
            label="Powód"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText={invalidReason ?? ' '}
            slotProps={{ htmlInput: { 'data-testid': 'settlement-correction-reason' } }}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button
          variant="contained"
          disabled={busy || parsed === null || invalidReason !== null}
          onClick={() => parsed !== null && onConfirm({ amount: parsed, currency, reason: reason.trim() })}
          data-testid="settlement-correction-confirm"
        >
          Dodaj
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const InvoicingDialog = ({
  open,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (note?: string) => void;
}) => {
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) setNote('');
  }, [open]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Przekaż do fakturowania</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Rozliczenie dostanie status „przekazane do fakturowania” i nie będzie już można go zmienić. Faktury
          aplikacja nie wystawia ani nigdzie nie wysyła — robi się ją poza aplikacją.
        </Typography>
        <TextField
          fullWidth
          label="Notatka (opcjonalnie)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'settlement-invoicing-note', maxLength: MAX_REASON_LENGTH } }}
        />
        {error ? <Alert severity="error">{error}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={() => onConfirm(note.trim() || undefined)}
          data-testid="settlement-invoicing-confirm"
        >
          Przekaż
        </Button>
      </DialogActions>
    </Dialog>
  );
};
